import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ArrowUpCircle, ArrowDownCircle, Plus, Building2, User, HandCoins, FileText, Trash2, Receipt, Eye, Undo2 } from 'lucide-react';

export function PaymentsTabContent({
  showDeletedOps, setShowDeletedOps, canCreatePagos, onNewPaymentOrder, filteredPaymentOrders,
  formatDate, formatCurrency, openSettleDialog, downloadPdf, canDeleteOp, onDeleteOp,
  canUnsettle, onUnsettle,
}) {
  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <ArrowDownCircle className="w-5 h-5 text-red-500" />
          Órdenes de Pago (Egresos)
        </h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="payments-show-deleted-toggle">
            <input type="checkbox" checked={showDeletedOps} onChange={(e) => setShowDeletedOps(e.target.checked)} className="h-4 w-4" />
            <span>Mostrar eliminadas</span>
          </label>
          {canCreatePagos && (
            <Button onClick={onNewPaymentOrder} data-testid="new-payment-order-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Orden de Pago
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Caja/Banco</TableHead>
                <TableHead>Concepto</TableHead>
                <TableHead>Destinatario</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPaymentOrders.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No hay órdenes de pago que coincidan con los filtros</TableCell></TableRow>
              ) : filteredPaymentOrders.map(po => (
                <TableRow key={po.payment_order_id} className={po.deleted_at ? 'opacity-60 bg-rose-50/40' : ''}>
                  <TableCell className="font-mono">{po.payment_order_number}{po.deleted_at && <Badge className="ml-2 bg-rose-500" data-testid={`po-deleted-badge-${po.payment_order_id}`}>Eliminada</Badge>}</TableCell>
                  <TableCell>{formatDate(po.date)}</TableCell>
                  <TableCell>
                    {po.cash_bank_name}
                    {po.outside_user_cash_banks && (
                      <Badge variant="outline" className="ml-2 text-[10px] py-0 px-1.5 border-amber-400 text-amber-700 bg-amber-50" data-testid={`po-outside-badge-${po.payment_order_id}`} title="Anticipo abierto en una caja fuera de tu alcance. Podés rendirlo, pero no ver saldo ni movimientos.">
                        Fuera de tus cajas
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{po.op_type === 'advance' ? <span className="italic text-muted-foreground">{po.expense_concept_name || 'Anticipo a rendir'}</span> : po.expense_concept_name}</TableCell>
                  <TableCell>
                    {po.recipient_type === 'supplier' ? (
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{po.supplier_name}</span>
                    ) : (
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{po.recipient_name}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {po.op_type === 'advance' ? (
                      po.advance_status === 'open'
                        ? <Badge className="bg-amber-500" data-testid={`advance-open-${po.payment_order_id}`}>Anticipo · Abierto</Badge>
                        : <Badge className="bg-emerald-600" data-testid={`advance-closed-${po.payment_order_id}`}>Anticipo · Rendido</Badge>
                    ) : <Badge variant="outline">Pago</Badge>}
                  </TableCell>
                  <TableCell className="text-right font-medium text-red-600">{formatCurrency(po.total_amount)}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      {po.op_type === 'advance' && po.advance_status === 'open' && canCreatePagos && (
                        <Button variant="outline" size="sm" className="h-8 text-amber-700 border-amber-300" onClick={() => openSettleDialog(po)} data-testid={`settle-advance-${po.payment_order_id}`}>
                          <HandCoins className="w-4 h-4 mr-1" /> Rendir
                        </Button>
                      )}
                      {po.op_type === 'advance' && po.advance_status === 'closed' && (
                        <Button variant="ghost" size="sm" data-testid={`settlement-pdf-${po.payment_order_id}`}
                          onClick={() => downloadPdf(`/treasury/payment-orders/${po.payment_order_id}/settlement.pdf`, `Rendicion_${po.payment_order_number}.pdf`, 'Generando rendición...')}
                          title="Comprobante de rendición">
                          <FileText className="w-4 h-4" />
                        </Button>
                      )}
                      {po.op_type === 'advance' && po.advance_status === 'closed' && !po.deleted_at && canUnsettle && (
                        <Button variant="outline" size="sm" className="h-8 text-amber-700 border-amber-300"
                          onClick={() => onUnsettle(po)} data-testid={`unsettle-advance-${po.payment_order_id}`}
                          title="Anular la rendición y reabrir el anticipo">
                          <Undo2 className="w-4 h-4 mr-1" /> Anular rendición
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" data-testid={`print-payment-order-${po.payment_order_id}`}
                        onClick={() => downloadPdf(`/treasury/payment-orders/${po.payment_order_id}/pdf`, `Orden_Pago_${po.payment_order_number}.pdf`, 'Generando Orden de Pago...')}
                        title="Imprimir Orden de Pago">
                        <FileText className="w-4 h-4" />
                      </Button>
                      {!po.deleted_at && canDeleteOp && (
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          onClick={() => onDeleteOp(po)} data-testid={`delete-op-${po.payment_order_id}`} title="Eliminar OP (reversa todo)">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {po.deleted_at && (
                        <span className="text-xs text-rose-700 italic" title={po.deletion_reason}>por {po.deleted_by_name || '—'}</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}

export function ChecksTabContent({
  checks, filteredChecks, formatCurrency, formatDate, getCheckStatusBadge, canViewValores,
  payIssuedCheck, rejectIssuedCheck, onDeposit, handleUseInPayment, handleReturnCheck,
}) {
  const pending = checks.filter(c => c.status === 'pending');
  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Receipt className="w-5 h-5 text-orange-500" />
          Valores en Cartera
          {pending.length > 0 && (
            <Badge className="bg-amber-100 text-amber-800 ml-2">{pending.length} pendientes</Badge>
          )}
        </h2>
        <p className="text-sm text-muted-foreground">Cheques recibidos (Cobranzas) y cheques de pago diferido / e.Cheq emitidos (Órdenes de Pago)</p>
      </div>

      {pending.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <Card><CardContent className="pt-3 pb-3 text-center"><p className="text-xs text-muted-foreground">Pendientes</p><p className="text-lg font-bold">{pending.length}</p></CardContent></Card>
          <Card><CardContent className="pt-3 pb-3 text-center"><p className="text-xs text-muted-foreground">Monto en Cartera</p><p className="text-lg font-bold text-amber-600">{formatCurrency(pending.reduce((s, c) => s + (c.amount || 0), 0))}</p></CardContent></Card>
          <Card><CardContent className="pt-3 pb-3 text-center"><p className="text-xs text-muted-foreground">Vencidos</p><p className="text-lg font-bold text-red-600">{pending.filter(c => c.due_date && new Date(c.due_date) < new Date()).length}</p></CardContent></Card>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nro Cheque</TableHead>
                <TableHead>Emisor</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Vencimiento</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...filteredChecks].sort((a, b) => {
                if (a.status === 'pending' && b.status !== 'pending') return -1;
                if (a.status !== 'pending' && b.status === 'pending') return 1;
                const da = a.due_date ? new Date(a.due_date) : new Date('9999-12-31');
                const db2 = b.due_date ? new Date(b.due_date) : new Date('9999-12-31');
                return da - db2;
              }).map(chk => {
                const isOverdue = chk.status === 'pending' && chk.due_date && new Date(chk.due_date) < new Date();
                const isDueSoon = chk.status === 'pending' && chk.due_date && !isOverdue &&
                  new Date(chk.due_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                return (
                  <TableRow key={chk.check_id} className={isOverdue ? 'bg-red-50' : isDueSoon ? 'bg-amber-50' : ''}>
                    <TableCell className="font-mono">{chk.check_number}</TableCell>
                    <TableCell>
                      {chk.issuer}
                      {chk.direction === 'issued'
                        ? <Badge className="ml-2 bg-rose-100 text-rose-700 text-[10px]">Emitido · diferido</Badge>
                        : <Badge variant="outline" className="ml-2 text-[10px]">Recibido</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{chk.direction === 'issued' ? (chk.delivered_to || chk.notes || '-') : (chk.notes || '-')}</TableCell>
                    <TableCell>
                      <span className={isOverdue ? 'text-red-600 font-semibold' : isDueSoon ? 'text-amber-600 font-medium' : ''}>{formatDate(chk.due_date)}</span>
                      {isOverdue && <span className="ml-1 text-xs text-red-500">VENCIDO</span>}
                      {isDueSoon && <span className="ml-1 text-xs text-amber-500">PRONTO</span>}
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(chk.amount, chk.currency)}</TableCell>
                    <TableCell className="text-center">{getCheckStatusBadge(chk.status)}</TableCell>
                    <TableCell className="text-center">
                      {chk.direction === 'issued' ? (
                        <>
                          {chk.status === 'pending' && canViewValores && (
                            <div className="flex justify-center gap-1" data-testid={`issued-check-actions-${chk.check_id}`}>
                              <Button variant="ghost" size="sm" onClick={() => payIssuedCheck(chk)} className="text-green-600 text-xs" title="Marcar como pagado (debita banco origen)" data-testid={`pay-issued-${chk.check_id}`}>Pagar</Button>
                              <Button variant="ghost" size="sm" onClick={() => rejectIssuedCheck(chk)} className="text-red-600 text-xs" title="Marcar como rechazado (reactiva la deuda)" data-testid={`reject-issued-${chk.check_id}`}>Rechazar</Button>
                            </div>
                          )}
                          {chk.status === 'paid' && (
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-xs text-muted-foreground">{chk.payment_order_number || ''} · {chk.delivered_to || '-'}</span>
                              {canViewValores && (
                                <Button variant="ghost" size="sm" onClick={() => rejectIssuedCheck(chk)} className="text-red-600 text-xs h-6" title="Marcar como rechazado">Rechazar</Button>
                              )}
                            </div>
                          )}
                          {chk.status === 'rejected' && (
                            <span className="text-xs text-red-600">{chk.return_reason || 'Rechazado'}</span>
                          )}
                        </>
                      ) : (
                        <>
                          {chk.status === 'pending' && canViewValores && (
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => onDeposit(chk)} className="text-green-600 text-xs" title="Depositar en cuenta bancaria">Depositar</Button>
                              <Button variant="ghost" size="sm" onClick={() => handleUseInPayment(chk)} className="text-blue-600 text-xs" title="Entregar como medio de pago">Usar en OP</Button>
                              <Button variant="ghost" size="sm" onClick={() => handleReturnCheck(chk)} className="text-red-600 text-xs" title="Marcar como rechazado">Rechazar</Button>
                            </div>
                          )}
                          {chk.status === 'deposited' && (
                            <span className="text-xs text-muted-foreground">Dep. en {chk.deposited_to_name || '-'}</span>
                          )}
                          {chk.status === 'delivered' && (
                            <span className="text-xs text-muted-foreground">
                              {chk.payment_order_number
                                ? <><span className="font-semibold text-indigo-700">{chk.payment_order_number}</span> - {chk.delivered_to || '-'}</>
                                : <>Entregado a {chk.delivered_to || '-'}</>}
                            </span>
                          )}
                          {chk.status === 'returned' && (
                            <span className="text-xs text-red-600">{chk.return_reason || 'Sin motivo'}</span>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {checks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No hay valores en cartera. Se generan automáticamente desde Cobranzas (cheques recibidos) y desde Órdenes de Pago con cheque diferido / e.Cheq.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}

export function CollectionsTabContent({ canCreateCobranzas, onNewCollection, filteredCollections, formatDate, formatCurrency, downloadPdf, canDeleteCollection, onDeleteCollection }) {
  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <ArrowUpCircle className="w-5 h-5 text-green-500" />
          Cobranzas (Ingresos)
        </h2>
        {canCreateCobranzas && (
          <Button onClick={onNewCollection} data-testid="new-collection-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Cobranza
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Caja/Banco</TableHead>
                <TableHead>Concepto</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="text-center">Recibo</TableHead>
                {canDeleteCollection && <TableHead className="text-center">Eliminar</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCollections.length === 0 ? (
                <TableRow><TableCell colSpan={canDeleteCollection ? 8 : 7} className="text-center text-muted-foreground py-8">No hay cobranzas que coincidan con los filtros</TableCell></TableRow>
              ) : filteredCollections.map(c => (
                <TableRow key={c.collection_id} className={c.deleted_at ? 'opacity-60' : ''} data-testid={`collection-row-${c.collection_number}`}>
                  <TableCell className="font-mono">
                    {c.collection_number}
                    {c.deleted_at && (
                      <span className="ml-2 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-medium text-white" data-testid={`collection-deleted-badge-${c.collection_number}`}>
                        ELIMINADA
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(c.date)}</TableCell>
                  <TableCell>{c.cash_bank_name}</TableCell>
                  <TableCell>{c.income_concept_name}</TableCell>
                  <TableCell>
                    {c.client_name || '-'}
                    {c.deleted_at && (
                      <span className="block text-xs text-muted-foreground">
                        Eliminada por {c.deleted_by_name} · {c.deletion_reason}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={`text-right font-medium ${c.deleted_at ? 'text-muted-foreground line-through' : 'text-green-600'}`}>{formatCurrency(c.total_amount)}</TableCell>
                  <TableCell className="text-center">
                    <Button variant="ghost" size="sm" data-testid={`print-receipt-${c.collection_id}`}
                      onClick={() => downloadPdf(`/treasury/collections/${c.collection_id}/pdf`, `Recibo_X_${c.collection_number}.pdf`, 'Generando Recibo X...')}
                      title="Imprimir Recibo X">
                      <FileText className="w-4 h-4" />
                    </Button>
                  </TableCell>
                  {canDeleteCollection && (
                    <TableCell className="text-center">
                      {!c.deleted_at && (
                        <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700"
                          onClick={() => onDeleteCollection(c)} title="Eliminar cobranza (reversa todo)"
                          data-testid={`delete-collection-${c.collection_number}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}

const MOVEMENT_TYPE_LABELS = {
  collection: 'Cobranza', payment_order: 'Orden de Pago', adjustment: 'Ajuste',
  transfer_out: 'Transf. Salida', transfer_in: 'Transf. Entrada', check_deposit: 'Depósito Cheque',
  check_return: 'Cheque Rechazado', reconciliation_adjustment: 'Ajuste Conciliación', cash_count_adjustment: 'Ajuste Arqueo',
  advance: 'Anticipo', advance_settlement: 'Rendición Anticipo', initial_balance: 'Saldo Inicial',
};

export function LedgerTabContent({ filteredMovements, formatDate, formatCurrency, onViewMovement }) {
  const live = filteredMovements.filter(m => !m.deleted_at);
  const totalDebit = live.reduce((s, m) => s + (Number(m.debit) || 0), 0);
  const totalCredit = live.reduce((s, m) => s + (Number(m.credit) || 0), 0);
  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Libro Diario de Tesorería
        </h2>
        <p className="text-sm text-muted-foreground">
          {live.length} movimiento(s) vigente(s) · Debe {formatCurrency(totalDebit)} · Haber {formatCurrency(totalCredit)}
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Caja/Banco</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Referencia</TableHead>
                <TableHead>Concepto</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead className="text-right">Debe</TableHead>
                <TableHead className="text-right">Haber</TableHead>
                <TableHead className="text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMovements.map(m => (
                <TableRow key={m.movement_id} className={m.deleted_at ? 'opacity-60' : ''} data-testid={`movement-row-${m.movement_id}`}>
                  <TableCell>{formatDate(m.date || m.created_at)}</TableCell>
                  <TableCell>{m.cash_bank_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type}</Badge>
                    {m.deleted_at && (
                      <span className="ml-1 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-medium text-white" data-testid={`movement-deleted-badge-${m.movement_id}`}>ELIMINADO</span>
                    )}
                  </TableCell>
                  <TableCell className={`font-mono text-sm ${m.deleted_at ? 'line-through' : ''}`}>{m.reference_number || '-'}</TableCell>
                  <TableCell className="text-sm">
                    {m.concept_name}
                    {m.deleted_at && (
                      <span className="block text-xs text-muted-foreground">Eliminado por {m.deleted_by_name} · {m.deletion_reason}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{m.created_by_name || '-'}</TableCell>
                  <TableCell className={`text-right ${m.deleted_at ? 'text-muted-foreground line-through' : 'text-green-600'}`}>{m.debit > 0 ? formatCurrency(m.debit) : '-'}</TableCell>
                  <TableCell className={`text-right ${m.deleted_at ? 'text-muted-foreground line-through' : 'text-red-600'}`}>{m.credit > 0 ? formatCurrency(m.credit) : '-'}</TableCell>
                  <TableCell className="text-center">
                    <Button variant="ghost" size="sm" data-testid={`view-movement-${m.movement_id}`} onClick={() => onViewMovement(m)} title="Ver detalle">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredMovements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No hay movimientos registrados</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}

